/**
 * Header Rendering Module
 *
 * Handles rendering of the grid header row with sorting and resize affordances.
 * Supports custom header renderers via `headerRenderer` (full control) and
 * `headerLabelRenderer` (label only) column properties.
 */

import { GridClasses } from '../constants';
import { toIconAttr } from '../plugin/base-plugin';
import type { ColumnInternal, GridHost, GridIcons, HeaderCellContext, IconValue, InternalGrid } from '../types';
import { addPart } from './columns';
import { sanitizeToFragment, setSanitizedHTML } from './sanitize';
import { toggleSort } from './sorting';

// #region Helper Functions
/**
 * Check if a column is sortable, respecting both column-level and grid-level settings.
 * Grid-wide `sortable: false` disables sorting for all columns.
 * Grid-wide `sortable: true` (or undefined) allows column-level `sortable` to control behavior.
 */
function isColumnSortable(grid: InternalGrid, col: ColumnInternal): boolean {
  // Grid-wide sortable defaults to true if not specified
  const gridSortable = grid.effectiveConfig?.sortable !== false;
  return gridSortable && col.sortable === true;
}

/**
 * Check if a column is resizable, respecting both column-level and grid-level settings.
 * Grid-wide `resizable: false` disables resizing for all columns.
 * Grid-wide `resizable: true` (or undefined) allows column-level `resizable` to control behavior.
 */
function isColumnResizable(grid: InternalGrid, col: ColumnInternal): boolean {
  // Grid-wide resizable defaults to true if not specified
  const gridResizable = grid.effectiveConfig?.resizable !== false;
  // Column-level resizable defaults to true if not specified
  return gridResizable && col.resizable !== false;
}

/**
 * Set an icon value on an element. Handles both string and HTMLElement icons.
 */
function setIcon(element: HTMLElement, icon: IconValue): void {
  if (typeof icon === 'string') {
    setSanitizedHTML(element, icon);
  } else if (icon instanceof HTMLElement) {
    element.replaceChildren(icon.cloneNode(true));
  }
}

/**
 * Create a sort indicator element for a column.
 * Uses CSS-first icon rendering: sets data-icon for CSS pseudo-elements,
 * only injects DOM content if user provided a JS icon override.
 */
function createSortIndicator(grid: InternalGrid, col: ColumnInternal): HTMLElement {
  const icon = document.createElement('span');
  addPart(icon, 'sort-indicator');
  // Decorative. The sort state is carried by `aria-sort` on the cell, so the
  // glyph adds nothing but noise — and because it is drawn with a CSS
  // `::before`, Chrome folds its content into the header's name-from-content,
  // announcing "Name ⇅" instead of "Name".
  icon.setAttribute('aria-hidden', 'true');
  const active = grid._sortState?.field === col.field ? grid._sortState.direction : 0;
  const iconKey: keyof GridIcons = active === 1 ? 'sortAsc' : active === -1 ? 'sortDesc' : 'sortNone';

  // Set data-icon for CSS icon rendering
  icon.dataset.icon = toIconAttr(iconKey);

  // Only inject content if user explicitly configured a JS icon override
  const userIcon = grid.icons?.[iconKey];
  if (userIcon !== undefined) {
    setIcon(icon, userIcon);
  }

  return icon;
}

/**
 * Create a resize handle element.
 *
 * The handle is both a drag target and a click target: dragging resizes
 * continuously, while a plain tap opens the click-only width control. That
 * second path is what satisfies WCAG 2.2 SC 2.5.7 "Dragging Movements", so the
 * handle must be exposed to assistive tech (a pointer user driving a speech or
 * head-pointer emulator needs an accessible name to target it) rather than
 * hidden with `aria-hidden`. `tabindex="-1"` keeps it out of the tab order —
 * one extra tab stop per column would swamp keyboard header navigation.
 */
function createResizeHandle(grid: InternalGrid, colIndex: number, cell: HTMLElement): HTMLElement {
  const handle = document.createElement('div');
  handle.className = 'resize-handle';
  handle.setAttribute('role', 'button');
  handle.setAttribute('tabindex', '-1');
  const col = grid._visibleColumns[colIndex];
  handle.setAttribute('aria-label', `Width of column ${String(col?.header ?? col?.field ?? colIndex + 1)}`);
  handle.addEventListener('pointerdown', (e: PointerEvent) => {
    // Only the primary mouse button starts a resize. Without this, a right-click
    // on the handle would preventDefault() and swallow the context menu.
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    e.stopPropagation();
    e.preventDefault();
    // Capture on the handle itself so the drag keeps tracking once the pointer
    // leaves the header cell, or the header is re-rendered mid-drag.
    grid._resizeController.start(e, colIndex, cell, handle);
  });
  handle.addEventListener('dblclick', (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    grid._resizeController.resetColumn(colIndex);
  });
  return handle;
}

/**
 * Setup sorting click/keyboard handlers for a header cell.
 */
function setupSortHandlers(grid: GridHost, col: ColumnInternal, colIndex: number, cell: HTMLElement): void {
  cell.classList.add(GridClasses.SORTABLE);
  cell.tabIndex = 0;
  const active = grid._sortState?.field === col.field ? grid._sortState.direction : 0;
  cell.setAttribute('aria-sort', active === 0 ? 'none' : active === 1 ? 'ascending' : 'descending');

  cell.addEventListener('click', (e) => {
    if (grid._resizeController?.isResizing) return;
    if (grid._dispatchHeaderClick?.(e, col, cell)) return;
    toggleSort(grid, col);
  });
  cell.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (grid._dispatchHeaderClick?.(e, col, cell)) return;
      toggleSort(grid, col);
    }
  });
}

/**
 * Append renderer output to cell element.
 * Handles Node, string (with sanitization), or null/void (no-op).
 */
function appendRendererOutput(cell: HTMLElement, output: Node | string | void | null): void {
  if (output == null) return;
  if (typeof output === 'string') {
    cell.appendChild(sanitizeToFragment(output));
  } else if (output instanceof Node) {
    cell.appendChild(output);
  }
}
// #endregion

// #region Render Header
/**
 * Rebuild the header row DOM based on current column configuration, attaching
 * sorting and resize affordances where enabled.
 *
 * Rendering precedence:
 * 1. `headerRenderer` - Full control over header cell content
 * 2. `headerLabelRenderer` - Custom label, framework handles icons/interactions
 * 3. `__headerTemplate` - Light DOM template (framework adapter)
 * 4. `header` property - Plain text header
 * 5. `field` - Fallback to field name
 */
export function renderHeader(grid: GridHost): void {
  grid._headerRowEl = grid.findHeaderRow!();
  const headerRow = grid._headerRowEl as HTMLElement;

  // Guard: DOM may not be built yet
  if (!headerRow) {
    return;
  }

  headerRow.innerHTML = '';

  grid._visibleColumns.forEach((col: ColumnInternal, i: number) => {
    const cell = document.createElement('div');
    cell.className = 'cell';
    addPart(cell, 'header-cell');
    cell.setAttribute('role', 'columnheader');

    // aria-colindex is 1-based
    cell.setAttribute('aria-colindex', String(i + 1));
    cell.setAttribute('data-field', col.field);
    cell.setAttribute('data-col', String(i)); // Add data-col for consistency with body cells
    if (col.type) cell.setAttribute('data-type', col.type);

    // Compute header value and sort state for context
    const headerValue = col.header ?? col.field;
    const sortDirection = grid._sortState?.field === col.field ? grid._sortState.direction : 0;
    const sortState: 'asc' | 'desc' | null = sortDirection === 1 ? 'asc' : sortDirection === -1 ? 'desc' : null;

    // Check for headerRenderer (full control mode)
    if (col.headerRenderer) {
      // Create context with helper functions
      const ctx: HeaderCellContext<any> = {
        column: col,
        value: headerValue,
        sortState,
        filterActive: false, // Will be set by FilteringPlugin if active
        cellEl: cell,
        renderSortIcon: () => (isColumnSortable(grid, col) ? createSortIndicator(grid, col) : null),
        renderFilterButton: () => null, // FilteringPlugin adds filter button via afterRender
      };

      const output = col.headerRenderer(ctx);
      appendRendererOutput(cell, output);

      // Setup sort handlers if sortable (user may not have included sort icon but still want click-to-sort)
      if (isColumnSortable(grid, col)) {
        setupSortHandlers(grid, col, i, cell);
      }

      // Always add resize handle if resizable (not user's responsibility)
      if (isColumnResizable(grid, col)) {
        cell.classList.add('resizable');
        cell.appendChild(createResizeHandle(grid, i, cell));
      }
    }
    // Check for headerLabelRenderer (label-only mode)
    else if (col.headerLabelRenderer) {
      const ctx = {
        column: col,
        value: headerValue,
      };

      const output = col.headerLabelRenderer(ctx);
      // Wrap output in a span for consistency with default rendering
      const span = document.createElement('span');
      if (output == null) {
        span.textContent = headerValue;
      } else if (typeof output === 'string') {
        setSanitizedHTML(span, output);
      } else if (output instanceof Node) {
        span.appendChild(output);
      }
      cell.appendChild(span);

      // Framework handles the rest: sort icon, resize handle
      if (isColumnSortable(grid, col)) {
        setupSortHandlers(grid, col, i, cell);
        cell.appendChild(createSortIndicator(grid, col));
      }
      if (isColumnResizable(grid, col)) {
        cell.classList.add('resizable');
        cell.appendChild(createResizeHandle(grid, i, cell));
      }
    }
    // Light DOM template (framework adapter)
    else if (col.__headerTemplate) {
      Array.from(col.__headerTemplate.childNodes).forEach((n) => cell.appendChild(n.cloneNode(true)));

      // Standard affordances
      if (isColumnSortable(grid, col)) {
        setupSortHandlers(grid, col, i, cell);
        cell.appendChild(createSortIndicator(grid, col));
      }
      if (isColumnResizable(grid, col)) {
        cell.classList.add('resizable');
        cell.appendChild(createResizeHandle(grid, i, cell));
      }
    }
    // Default: plain text header
    else {
      const span = document.createElement('span');
      span.textContent = headerValue;
      cell.appendChild(span);

      // Pin the accessible name to the column label.
      //
      // `columnheader` takes its name from content, and "content" includes every
      // descendant's text alternative — so the resize handle (a `role="button"`
      // with `aria-label="Width of column Name"`, deliberately exposed for SC
      // 2.5.7) and the filter button plugins inject here both get concatenated
      // in. Without this, every header in the grid announces as
      // "Name ⇅ Width of column Name". An explicit label stops the traversal;
      // it is safe against SC 2.5.3 Label in Name because it is character-for-
      // character the visible text. Only the default branch does this — a
      // consumer-supplied `headerRenderer` owns its own naming.
      cell.setAttribute('aria-label', headerValue);

      // Standard affordances
      if (isColumnSortable(grid, col)) {
        setupSortHandlers(grid, col, i, cell);
        cell.appendChild(createSortIndicator(grid, col));
      }
      if (isColumnResizable(grid, col)) {
        cell.classList.add('resizable');
        cell.appendChild(createResizeHandle(grid, i, cell));
      }
    }

    headerRow.appendChild(cell);
  });

  // Ensure every sortable header has a baseline aria-sort if not already set during construction.
  headerRow.querySelectorAll(`.cell.${GridClasses.SORTABLE}`).forEach((el) => {
    if (!el.getAttribute('aria-sort')) el.setAttribute('aria-sort', 'none');
  });

  // Set ARIA role only if header has children (role="row" requires columnheader children)
  // When grid is cleared with 0 columns, the header row should not have role="row"
  if (headerRow.children.length > 0) {
    headerRow.setAttribute('role', 'row');
    headerRow.setAttribute('aria-rowindex', '1');
  } else {
    headerRow.removeAttribute('role');
    headerRow.removeAttribute('aria-rowindex');
  }
}
// #endregion
